<template>
  <!-- 纯对话内容区（侧栏由 student-layout 承载） -->
  <div class="chat-container">
    <!-- 顶部导航栏 -->
    <div class="chat-header">
      <div class="header-left">
        <div class="header-title">实时对答</div>
        <span
          class="connection-status"
          :class="{ connected: isConnected }"
          @click="!isConnected && connectWebSocket()"
        >
          {{ isConnected ? '已连接' : '未连接' }}
        </span>
      </div>
      <div class="header-actions">
        <button
          v-if="devNo"
          class="header-action-btn"
          type="button"
          @click="openPhotoDialog"
        >
          <i class="fa fa-camera"></i>
          <span>拍照查看</span>
        </button>
        <el-dropdown class="header-right" trigger="click" @command="handleCommand">
          <button class="header-more-btn" type="button">
            <i class="el-icon-more"></i>
          </button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="search">查找聊天内容</el-dropdown-item>
            <el-dropdown-item command="reconnect" v-if="!isConnected">重新连接</el-dropdown-item>
            <el-dropdown-item command="disconnect" v-if="isConnected">断开连接</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>

    <!-- 搜索弹窗 -->
    <el-dialog
      :visible.sync="searchDialogVisible"
      width="100%"
      :modal-append-to-body="false"
      custom-class="search-dialog"
      :show-close="false"
    >
      <div class="search-container">
        <!-- 搜索头部 -->
        <div class="search-header">
          <div class="search-box">
            <i class="el-icon-search"></i>
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索聊天内容"
              ref="searchInput"
            />
            <i v-if="searchKeyword" class="el-icon-circle-close" @click="clearSearch"></i>
          </div>
          <div class="cancel-btn" @click="closeSearch">取消</div>
        </div>

        <!-- 搜索结果 -->
        <div class="search-results">
          <div v-if="searchKeyword && filteredResults.length === 0" class="no-result">
            暂无搜索结果
          </div>
          <div v-else>
            <div
              v-for="(result, index) in filteredResults"
              :key="index"
              class="result-item"
            >
              <img :src="result.avatar" class="result-avatar" />
              <div class="result-content">
                <div class="result-name">{{ result.name }}</div>
                <div class="result-message" v-html="highlightKeyword(result.content)"></div>
              </div>
              <div class="result-date">{{ result.date }}</div>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 聊天消息区域 -->
    <div class="chat-messages" :class="{ 'has-cs-fab': !showChatInput && !showLoading }" ref="messagesContainer">
      <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
        <!-- 时间戳 -->
        <div v-if="message.showTime" class="message-time">{{ message.time }}</div>

        <!-- 消息内容 -->
        <div class="message-item" :class="message.type">
          <!-- 头像 -->
          <div class="avatar">
            <img :src="message.avatar" alt="avatar" />
          </div>

          <!-- 加载消息弹窗 -->
          <div v-if="message.isLoadingMessage" class="loading-message-card">
            <div class="loading-message-text">{{ message.loadingText }}</div>
            <div class="loading-message-gif">
              <img :src="loadingGif" alt="加载中..." />
            </div>
          </div>

          <!-- 处理失败提示 -->
          <div v-else-if="message.isProcessError" class="process-error-card">
            <div class="process-error-icon">
              <i class="el-icon-warning-outline"></i>
            </div>
            <div class="process-error-text">{{ message.content }}</div>
          </div>

          <!-- 普通消息气泡 -->
          <div v-else-if="!message.isRealTimeAnswer && !message.isPdfContent && !message.isOcrResult && !message.isExercisePaper && !message.isHomeworkCorrection" class="message-bubble">
            <div class="bubble-content">{{ message.content }}</div>
          </div>

          <!-- PDF 内容卡片 -->
          <div v-else-if="message.isPdfContent" class="pdf-content-card">
            <div class="pdf-header">
              <i class="el-icon-document"></i>
              <span>学习计划 PDF</span>
            </div>
            <div class="pdf-info">
              <i class="el-icon-document-checked pdf-icon"></i>
              <div class="pdf-text">
                <div class="pdf-title">学习计划文档</div>
                <div class="pdf-desc">点击下方按钮查看或下载PDF文件</div>
              </div>
            </div>
            <div class="pdf-actions">
              <span @click="openPdfInNewWindow(message.pdfUrl)" class="pdf-action-btn">
                <i class="el-icon-view"></i>
                在新窗口打开
              </span>
              <span @click="downloadPdf(message.pdfUrl)" class="pdf-action-btn">
                <i class="el-icon-download"></i>
                下载PDF
              </span>
            </div>
          </div>

          <!-- 自动出题练习卷卡片 (type === 7) -->
          <div v-else-if="message.isExercisePaper" class="exercise-paper-card">
            <div class="exercise-header">
              <i class="el-icon-notebook-2"></i>
              <span>练习卷已生成</span>
              <span v-if="message.processingTime" class="processing-time">
                ({{ (message.processingTime / 1000).toFixed(2) }}秒)
              </span>
              <div
                class="exercise-actions"
                v-if="message.exerciseData && message.exerciseData.questionList && message.exerciseData.questionList.length > 1"
              >
                <span class="action-btn" @click="toggleExercisePaperList(message.id)">
                  <i :class="isExercisePaperExpanded(message.id) ? 'el-icon-arrow-up' : 'el-icon-more'"></i>
                  {{ isExercisePaperExpanded(message.id) ? '收起' : '更多' }}
                </span>
              </div>
            </div>

            <div v-if="message.exerciseData && message.exerciseData.pdfUrl" class="exercise-summary">
              <div class="exercise-summary-main">
                <i class="el-icon-document-checked exercise-icon"></i>
                <div class="exercise-summary-text">
                  <div class="exercise-title">{{ message.exerciseData.paperName || '巩固练习' }}</div>
                  <div class="exercise-desc">
                    共 {{ message.exerciseData.questionCount || (message.exerciseData.questionList || []).length }} 题
                    <template v-if="message.exerciseData.questionList && message.exerciseData.questionList[0]">
                      · {{ message.exerciseData.questionList[0].subject || '' }}
                      {{ message.exerciseData.questionList[0].grade || '' }}
                    </template>
                  </div>
                </div>
              </div>
              <div class="exercise-pdf-actions">
                <span
                  v-if="message.exerciseData.pdfUrl"
                  @click="openPdfInNewWindow(message.exerciseData.pdfUrl)"
                  class="pdf-action-btn"
                >
                  <i class="el-icon-view"></i>
                  查看练习卷
                </span>
                <span
                  v-if="message.exerciseData.pdfUrl"
                  @click="downloadPdf(message.exerciseData.pdfUrl)"
                  class="pdf-action-btn"
                >
                  <i class="el-icon-download"></i>
                  下载练习卷
                </span>
                <span
                  v-if="message.exerciseData.answerPdfUrl"
                  @click="openPdfInNewWindow(message.exerciseData.answerPdfUrl)"
                  class="pdf-action-btn secondary"
                >
                  <i class="el-icon-document"></i>
                  查看答案卷
                </span>
              </div>
            </div>

            <div v-else class="exercise-empty">
              <i class="el-icon-warning-outline"></i>
              <p>{{ (message.exerciseData && message.exerciseData.tips) || message.content || '出题失败，请稍后再试' }}</p>
            </div>

            <div
              v-if="message.exerciseData && message.exerciseData.questionList && message.exerciseData.questionList.length"
              class="exercise-questions"
            >
              <div class="exercise-questions-title">
                <i class="el-icon-tickets"></i>
                题目预览（共 {{ message.exerciseData.questionList.length }} 题）
              </div>
              <div
                v-for="(item, idx) in visibleExerciseQuestions(message)"
                :key="item.id || idx"
                class="exercise-question-item"
                :class="{ expanded: isExerciseQuestionExpanded(message.id, idx) }"
              >
                <div class="exercise-question-head" @click="toggleExerciseQuestion(message.id, idx)">
                  <div class="exercise-question-number">{{ idx + 1 }}</div>
                  <div class="exercise-question-meta">
                    <span class="type-tag">{{ getQuestionTypeName(item.questionType) }}</span>
                    <span v-if="item.knowledgePointName" class="kp-tag">{{ item.knowledgePointName }}</span>
                    <span v-if="item.difficulty" class="diff-tag">难度 {{ item.difficulty }}</span>
                  </div>
                  <i
                    class="expand-icon"
                    :class="isExerciseQuestionExpanded(message.id, idx) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
                  ></i>
                </div>
                <div class="exercise-question-content math-content" v-html="formatExerciseContent(item.content)"></div>
                <div
                  v-if="parseOptionsContent(item.optionsContent).length"
                  class="exercise-options"
                >
                  <div
                    v-for="(opt, optIdx) in parseOptionsContent(item.optionsContent)"
                    :key="optIdx"
                    class="exercise-option"
                  >
                    <span class="option-label">{{ opt.option }}.</span>
                    <span class="option-text">{{ opt.text }}</span>
                  </div>
                </div>
                <div
                  v-if="isExerciseQuestionExpanded(message.id, idx)"
                  class="exercise-question-detail"
                >
                  <div v-if="item.answerContent" class="detail-row answer-row">
                    <span class="detail-label">答案</span>
                    <span class="detail-value">{{ item.answerContent }}</span>
                  </div>
                  <div v-if="item.analysis" class="detail-row analysis-row">
                    <span class="detail-label">解析</span>
                    <span class="detail-value math-content" v-html="formatExerciseContent(item.analysis)"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 作业批改卡片 (type === 5) -->
          <div v-else-if="message.isHomeworkCorrection" class="homework-correction-card">
            <div class="homework-header">
              <i class="el-icon-finished"></i>
              <span>作业批改</span>
              <span v-if="message.processingTime" class="processing-time">
                ({{ (message.processingTime / 1000).toFixed(2) }}秒)
              </span>
              <div class="homework-actions">
                <span
                  v-if="message.homeworkData && message.homeworkData.shootImg"
                  class="action-btn"
                  @click="showShootImage(message.homeworkData.shootImg)"
                >
                  <i class="el-icon-picture-outline"></i>
                  拍摄图片
                </span>
                <span
                  v-if="message.homeworkData && message.homeworkData.homeworkCorrectionId"
                  class="action-btn"
                  @click="openHomeworkDetail(message)"
                >
                  <i class="el-icon-info"></i>
                  查看详情
                </span>
              </div>
            </div>

            <div
              v-if="message.homeworkData && message.homeworkData.correctionImgUrl"
              class="homework-preview"
            >
              <img
                :src="message.homeworkData.correctionImgUrl"
                alt="批改结果"
                @click="showOcrImage(message.homeworkData.correctionImgUrl)"
              />
              <div class="homework-preview-caption">
                <span>{{ (message.homeworkData && message.homeworkData.answerStr) || '作业批改完成' }}</span>
                <span class="homework-preview-hint">点击查看批改大图</span>
              </div>
            </div>
            <div v-else class="homework-empty">
              <i class="el-icon-document-checked"></i>
              <p>{{ (message.homeworkData && message.homeworkData.answerStr) || message.content || '作业批改完成' }}</p>
            </div>

            <div class="homework-footer">
              <span class="homework-link-btn" @click="goToHomeworkPage">
                <i class="el-icon-right"></i>
                打开作业批改页
              </span>
            </div>
          </div>

          <!-- OCR 识别结果卡片 (kind === 3) -->
          <div v-else-if="message.isOcrResult" class="ocr-result-card">
            <div class="ocr-header">
              <i class="el-icon-document"></i>
              <span>错题识别</span>
              <span v-if="message.processingTime" class="processing-time">
                ({{ (message.processingTime / 1000).toFixed(2) }}秒)
              </span>
              <!-- 操作按钮 -->
              <div class="ocr-actions">
                <span
                  v-if="message.shootImg"
                  class="action-btn"
                  @click="showShootImage(message.shootImg)"
                >
                  <i class="el-icon-picture-outline"></i>
                  拍摄图片
                </span>
                <span
                  v-if="message.id"
                  class="action-btn"
                  @click="showMoreInfo(message.id)"
                >
                  <i class="el-icon-info"></i>
                  更多信息
                </span>
              </div>
            </div>
            <div v-if="message.ocrData && message.ocrData.length > 0" class="ocr-items">
              <div class="ocr-items-title">
                <i class="el-icon-tickets"></i>
                识别的题目（共 {{ message.ocrData.length }} 题）
              </div>
              <div
                v-for="(item, idx) in message.ocrData"
                :key="idx"
                class="ocr-item"
              >
                <div class="ocr-item-number">{{ idx + 1 }}</div>
                <div class="ocr-text math-content" v-html="item.text"></div>
                <div class="ocr-image-icon" @click="showOcrImage(item.paperCutImg)">
                  <i class="el-icon-picture-outline"></i>
                </div>
              </div>
            </div>
            <div v-else class="ocr-empty">
              <i class="el-icon-document-checked"></i>
              <p>错题搜集完成，暂无识别内容</p>
            </div>
            <!-- 跳转错题本 -->
            <div v-if="message.ocrData && message.ocrData.length > 0" class="ocr-footer">
              <div class="print-btns">
                <span class="print-btn" @click="goToErrorBook">
                  <i class="el-icon-notebook-2"></i>
                  查看错题本
                </span>
              </div>
            </div>
          </div>

          <!-- 实时解答卡片 -->
          <div v-else-if="message.isRealTimeAnswer" class="answer-card" :ref="'answerCard_' + index">
            <div class="answer-header">
              <i class="el-icon-edit-outline"></i>
              <span>实时解答</span>
              <span v-if="message.answerData.processingTime" class="processing-time">
                ({{ (message.answerData.processingTime / 1000).toFixed(2) }}秒)
              </span>
              <!-- 操作按钮 -->
              <div class="answer-actions">
                <span
                  v-if="message.answerData.ShootImg"
                  class="action-btn"
                  @click="showShootImage1(message)"
                >
                  <i class="el-icon-picture-outline"></i>
                  拍摄图片
                </span>
                <span
                  v-if="message.id"
                  class="action-btn"
                  @click="showMoreInfo(message.id)"
                >
                  <i class="el-icon-info"></i>
                  更多信息
                </span>
              </div>
            </div>

            <div class="answer-grid">
              <div class="answer-section question-answer-section" :class="{ error: !message.answerData.isTure }">
                <div class="section-title">
                  <span class="section-title-left">
                    <i class="el-icon-document"></i>
                    题目
                  </span>
                  <span
                    class="result-badge"
                    :class="message.answerData.isTure ? 'correct' : 'wrong'"
                  >
                    <i :class="message.answerData.isTure ? 'el-icon-success' : 'el-icon-error'"></i>
                    {{ message.answerData.msg }}
                  </span>
                </div>
                <div class="section-content math-content" v-html="message.answerData.ocrContent"></div>
              </div>

              <div class="answer-section analysis-section" v-if="message.answerData.preAnswer || message.answerData.answer">
                <div class="section-title" v-if="message.answerData.preAnswer">
                  <span class="section-title-left">
                    <i class="el-icon-reading"></i>
                    解析
                  </span>
                </div>
                <div class="section-content math-content" v-if="message.answerData.preAnswer" v-html="message.answerData.preAnswer"></div>
                <div class="answer-result-inline" v-if="message.answerData.answer">
                  <span class="result-label">正确答案</span>
                  <span class="answer-value">{{ message.answerData.answer }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- OCR 图片预览弹窗 -->
    <el-dialog
      :visible.sync="ocrImageDialogVisible"
      width="90%"
      custom-class="ocr-image-dialog"
      :show-close="true"
      append-to-body
    >
      <img :src="currentOcrImage" alt="题目图片" class="ocr-preview-image" />
    </el-dialog>

    <!-- 拍摄图片预览弹窗 -->
    <el-dialog
      :visible.sync="shootImageDialogVisible"
      width="90%"
      title="图片预览"
      custom-class="shoot-image-dialog"
      :show-close="true"
      append-to-body
      @closed="captureImgUrl = ''"
    >
      <div class="shoot-preview-grid" :class="{ 'single-image': !captureImgUrl }">
        <div class="shoot-preview-panel" v-if="captureImgUrl">
          <div class="shoot-preview-label">
            <i class="el-icon-scissors"></i>
            <span>错题截图</span>
          </div>
          <div class="shoot-preview-frame">
            <img :src="captureImgUrl" alt="错题截图" class="shoot-preview-image" />
          </div>
        </div>
        <div class="shoot-preview-panel" v-if="currentShootImage">
          <div class="shoot-preview-label">
            <i class="el-icon-camera"></i>
            <span>拍摄原图</span>
          </div>
          <div class="shoot-preview-frame">
            <img :src="currentShootImage" alt="拍摄原图" class="shoot-preview-image" />
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 作业批改详情弹窗 -->
    <el-dialog
      :visible.sync="homeworkDetailVisible"
      width="1000px"
      title="批改详情"
      custom-class="homework-detail-dialog"
      :show-close="true"
      append-to-body
      center
    >
      <div v-if="homeworkDetailLoading" class="homework-detail-loading">
        <i class="el-icon-loading"></i> 正在加载...
      </div>
      <div v-else-if="homeworkDetail" class="homework-detail-body">
        <div class="homework-detail-left">
          <img
            v-if="homeworkDetailImage"
            :src="homeworkDetailImage"
            class="homework-detail-image"
            @click="showOcrImage(homeworkDetailImage)"
          />
          <div v-else class="homework-detail-placeholder">
            <i class="el-icon-picture-outline"></i>
            <span>暂无批改图</span>
          </div>
          <div class="homework-detail-meta">
            <div>共 {{ homeworkDetail.questionCount || 0 }} 题 · 正确 {{ homeworkDetail.correctCount || 0 }} 题 · 正确率 {{ homeworkDetailRate }}%</div>
          </div>
        </div>
        <div class="homework-detail-right">
          <div class="homework-question-toolbar">
            <el-radio-group v-model="homeworkQuestionFilter" size="mini">
              <el-radio-button label="all">全部</el-radio-button>
              <el-radio-button label="wrong">错题</el-radio-button>
              <el-radio-button label="correct">正确</el-radio-button>
            </el-radio-group>
          </div>
          <div v-if="!filteredHomeworkQuestions.length" class="homework-detail-empty">暂无题目</div>
          <div v-else class="homework-question-list">
            <div
              v-for="question in filteredHomeworkQuestions"
              :key="question.id"
              class="homework-question-item"
              :class="{ wrong: !question.isCorrect }"
            >
              <div class="homework-question-head">
                <el-tag :type="question.isCorrect ? 'success' : 'danger'" size="mini">
                  {{ question.isCorrect ? '正确' : '错误' }}
                </el-tag>
                <span v-if="question.subject" class="homework-question-subject">{{ question.subject }}</span>
                <span v-if="question.knowledgePoint" class="homework-question-point">{{ question.knowledgePoint }}</span>
              </div>
              <img
                v-if="question.paperCutImg"
                :src="question.paperCutImg"
                class="homework-question-image"
                @click="showOcrImage(question.paperCutImg)"
              />
              <div v-if="question.text" class="homework-question-text">{{ question.text }}</div>
              <div v-if="question.answer" class="homework-question-answer">
                <span>参考答案：</span>{{ question.answer }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="goToHomeworkPage">打开作业批改页</el-button>
        <el-button @click="homeworkDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 更多信息弹窗 -->
    <el-dialog
      :visible.sync="moreInfoDialogVisible"
      width="80%"
      title="更多信息"
      custom-class="more-info-dialog"
      :show-close="true"
      append-to-body
    >
      <div class="more-info-content">
        <div class="info-item">
          <span class="info-label">记录ID：</span>
          <span class="info-value">{{ currentRecordId }}</span>
        </div>
      </div>
    </el-dialog>

    <!-- 拍照查看弹窗 -->
    <photo-dialog :visible.sync="photoDialogVisible" :dev-no="devNo" />

    <!-- 底部输入栏：默认收起，右侧客服图标展开 -->
    <div class="chat-footer" v-show="!showLoading" :class="{ 'is-collapsed': !showChatInput }">
      <div class="chat-input-row">
        <div v-show="showChatInput" class="chat-input">
          <button class="input-icon-btn" type="button" title="语音" @click="showVoice">
            <i class="el-icon-microphone"></i>
          </button>
          <div class="input-box">
            <input
              ref="chatInput"
              v-model="inputMessage"
              type="text"
              placeholder="向 Okaka 提问..."
              @keyup.enter="sendMessage"
            />
          </div>
          <button class="input-icon-btn" type="button" title="表情" @click="showEmoji">
            <i class="el-icon-smile"></i>
          </button>
          <button class="input-icon-btn" type="button" title="更多" @click="showMore">
            <i class="el-icon-circle-plus"></i>
          </button>
          <button
            class="send-btn"
            type="button"
            :disabled="!inputMessage || !inputMessage.trim()"
            @click="sendMessage"
          >
            <i class="el-icon-s-promotion"></i>
          </button>
        </div>
        <button
          class="chat-cs-btn"
          type="button"
          :class="{ active: showChatInput }"
          :title="showChatInput ? '收起输入框' : '向 Okaka 提问'"
          @click="toggleChatInput"
        >
          <i :class="showChatInput ? 'el-icon-close' : 'el-icon-edit-outline'"></i>
        </button>
      </div>
      <div v-show="showChatInput" class="ai-disclaimer">
        Okaka 服务是一款 AI 工具，其回答未必正确无误。
      </div>
    </div>

    <!-- Loading 区域 -->
    <div class="loading-container" v-show="showLoading">
      <img :src="loadingGif" alt="加载中..." class="loading-gif" />
    </div>

    <!-- 空闲屏保：50s 无触摸/点击 / 无业务 socket 消息 -->
    <div
      v-show="showIdleScreen"
      class="idle-screensaver"
      @click="resetIdleTimer"
      @touchstart.prevent="resetIdleTimer"
    >
      <div class="idle-clock">
        <div class="idle-date">{{ idleDateText }}</div>
        <div class="idle-time">{{ idleTimeText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { axios, wsConfig, getDevNo } from '@/config/axios'
import PhotoDialog from '@/components/PhotoDialog.vue'

export default {
  name: 'StudentRealTimeAnswers',
  components: {
    PhotoDialog
  },
  data() {
    return {
      inputMessage: '',
      showChatInput: false,
      searchDialogVisible: false,
      searchKeyword: '',
      // WebSocket 相关
      websocket: null,
      isConnected: false,
      serverUrl: wsConfig.serverUrl,
      // 是否人为主动断开（主动断开时不自动重连）
      wsManualClose: false,
      // 重连定时器 / 心跳定时器
      wsReconnectTimer: null,
      wsHeartbeatTimer: null,
      // 当前重连次数（成功连接后清零）
      wsReconnectAttempts: 0,
      // 心跳间隔、重连基础间隔（毫秒）
      wsHeartbeatInterval: 25000,
      wsReconnectBaseDelay: 3000,
      wsReconnectMaxDelay: 30000,
      devNo: '',
      messages: [],
      // 头像
      robotAvatar: require('@/assets/img/okaka.png'),
      userAvatar: require('@/assets/img/head.png'),
      // loading 状态
      showLoading: false,
      loadingGif: require('@/assets/img/load.gif'),
      // MathJax 相关
      mathJaxLoaded: false,
      // OCR 图片预览
      ocrImageDialogVisible: false,
      currentOcrImage: '',
      // 拍摄图片预览
      shootImageDialogVisible: false,
      currentShootImage: '',
      captureImgUrl:'',
      // 更多信息
      moreInfoDialogVisible: false,
      currentRecordId: '',
      // 拍照查看
      photoDialogVisible: false,
      // 搜索页跳转定位（历史未加载完时暂存）
      pendingLocatePayload: null,
      // 练习卷题目展开状态（key: `${chatHistoryId}-${idx}`）
      expandedExerciseQuestions: {},
      // 练习卷题目列表是否全部展开（key: chatHistoryId）
      expandedExercisePapers: {},
      // 作业批改详情
      homeworkDetailVisible: false,
      homeworkDetailLoading: false,
      homeworkDetail: null,
      homeworkQuestionFilter: 'all',
      // 空闲屏保
      showIdleScreen: false,
      idleDateText: '',
      idleTimeText: '',
      idleTimer: null,
      idleClockTimer: null,
      idleTimeoutMs: 50000
    }
  },
  computed: {
    filteredResults() {
      if (!this.searchKeyword) {
        return []
      }
      // 从实际消息中搜索
      return this.messages.filter(msg => {
        const content = msg.content || ''
        return content.includes(this.searchKeyword)
      })
    },
    recentConversations() {
      const seen = new Set()
      const list = []
      // 从新到旧取用户消息作为最近对话标题
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const msg = this.messages[i]
        if (msg.type !== 'sent' || !msg.content) continue
        const title = String(msg.content).replace(/\s+/g, ' ').trim()
        if (!title || seen.has(title)) continue
        seen.add(title)
        list.push({
          id: `recent-${i}`,
          title,
          messageIndex: i
        })
        if (list.length >= 20) break
      }
      return list
    },
    homeworkDetailImage() {
      const record = this.homeworkDetail
      if (!record) return ''
      return record.correctionImgUrl || record.annotatedImageUrl || record.dealImgUrl || record.orgImgUrl || ''
    },
    homeworkDetailRate() {
      const total = (this.homeworkDetail && this.homeworkDetail.questionCount) || 0
      if (!total) return 0
      return Math.round(((this.homeworkDetail.correctCount || 0) * 100) / total)
    },
    filteredHomeworkQuestions() {
      const list = (this.homeworkDetail && this.homeworkDetail.questionList) || []
      if (this.homeworkQuestionFilter === 'wrong') {
        return list.filter(item => !item.isCorrect)
      }
      if (this.homeworkQuestionFilter === 'correct') {
        return list.filter(item => item.isCorrect)
      }
      return list
    }
  },
  watch: {
    recentConversations: {
      immediate: true,
      handler(list) {
        this.$root.$emit('chat:recent-updated', list)
      }
    }
  },
  methods: {
    // 加载 MathJax
    loadMathJax() {
      if (this.mathJaxLoaded || window.MathJax) {
        this.mathJaxLoaded = true
        return Promise.resolve()
      }

      return new Promise((resolve) => {
        window.MathJax = {
          tex: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$', '$$'], ['\\[', '\\]']],
            processEscapes: true,
            processEnvironments: true,
            // 加载所有包
            packages: {'[+]': ['base', 'ams']},
            // 遇到未定义的命令时的处理
            macros: {},
            tags: 'ams'
          },
          svg: {
            fontCache: 'global'
          },
          options: {
            skipHtmlTags: ['script', 'noscript', 'style', 'textarea', 'pre', 'code'],
            processHtmlClass: 'math-content',
            ignoreHtmlClass: 'no-mathjax',
            // 启用详细的错误信息
            enableMenu: true
          },
          startup: {
            ready: () => {
              window.MathJax.startup.defaultReady()
              this.mathJaxLoaded = true
              resolve()
            }
          },
          // 注意：不要在这里覆盖 MathJax.typesetPromise（MathJax 会在加载后注入真正的方法）
        }

        const script = document.createElement('script')
        // 同时支持 TeX（$...$）与 MathML（<math>...</math>），避免 Math input error
        script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js'
        script.async = true
        script.onerror = () => {
          console.error('MathJax 脚本加载失败')
        }
        document.head.appendChild(script)
      })
    },

    // 渲染数学公式
    async renderMath() {
      await this.loadMathJax()
      if (window.MathJax && window.MathJax.typesetPromise) {
        await this.$nextTick()
        // 额外延迟确保 DOM 完全更新
        await new Promise(resolve => setTimeout(resolve, 100))
        try {
          const mathElements = this.$el.querySelectorAll('.math-content')
          if (mathElements.length > 0) {
            await window.MathJax.typesetClear(Array.from(mathElements))
            await window.MathJax.typesetPromise(Array.from(mathElements))
          }
        } catch (err) {
          console.error('MathJax 渲染错误:', err)
        }
      }
    },

    // 规范化 OCR/解析返回的 HTML，保证 MathJax 能稳定渲染（支持 LaTeX + MathML）
    cleanMathML(htmlString) {
      if (!htmlString) return htmlString

      let cleaned = htmlString

      // 0) 将“展示公式”（独占一行）统一转为“行内公式”
      //    目标：避免 MathJax 生成 mjx-container[display="true"] 导致居中/独占一行
      //    $$ ... $$ / \[ ... \] -> $ ... $
      cleaned = cleaned
        .replace(/\$\$([\s\S]*?)\$\$/g, (full, inner) => {
          const content = String(inner).replace(/\s+/g, ' ').trim()
          return `$${content}$`
        })
        .replace(/\\\[([\s\S]*?)\\\]/g, (full, inner) => {
          const content = String(inner).replace(/\s+/g, ' ').trim()
          return `$${content}$`
        })

      // 1) 去掉多余的 <html>/<body> 包裹，避免 v-html 注入后 DOM 结构异常
      cleaned = cleaned.replace(/<\/?(html|body)[^>]*>/gi, '')

      // 2) OCR 输出里常见 \" 多余转义：把 \" 还原成 "
      //    这能把 <div class=\"line\"> 修复成标准 HTML，避免后续处理/渲染异常
      cleaned = cleaned.replace(/\\"/g, '"')

      // 3) 移除 data-bbox（有时是 "..." / '...' / \"...\" 三种形式）
      cleaned = cleaned.replace(/\s+data-bbox=(?:"[^"]*"|'[^']*'|\\"[^\\"]*\\")/g, '')

      // 4) 修正常见的 LaTeX 命令“多一层反斜杠”的情况：\\frac -> \frac（仅对字母命令）
      //    避免 MathJax 将 \\ 当作换行命令导致解析错误
      cleaned = cleaned.replace(/\$([\s\S]*?)\$/g, (full, inner) => {
        const fixedInner = String(inner).replace(/\\\\(?=[a-zA-Z])/g, '\\')
        return `$${fixedInner}$`
      })

      // 5) 移除多余的空行/空白拼接
      cleaned = cleaned.replace(/(<\/div>)\s*(<div)/g, '$1$2')

      // 确保 LaTeX 公式中的反斜杠没有被转义
      // 检查是否存在被 HTML 转义的情况，如 &lt; &gt; &amp;
      if (cleaned.includes('&lt;') || cleaned.includes('&gt;') || cleaned.includes('&amp;')) {
        const textarea = document.createElement('textarea')
        textarea.innerHTML = cleaned
        cleaned = textarea.value
      }

      return cleaned
    },

    // 解析实时解答数据（用于 messageType === 2）
    parseRealTimeAnswerData(jsonString) {
      try {
        // 先从原始字符串中提取 answerStr 的值（保持"外层 JSON 字符串"里的原始转义）
        const answerStrMatch = jsonString.match(/"answerStr"\s*:\s*"((?:[^"\\]|\\.)*)"/s)
        if (!answerStrMatch) {
          console.error('无法从原始字符串中提取 answerStr')
          return null
        }

        const rawAnswerStr = answerStrMatch[1]

        // 外层 JSON 仍然正常解析，用来取 ShootImg / requestAnswerRecordId / processingTime
        const outerData = JSON.parse(jsonString)

        // 将 rawAnswerStr 按"JSON 字符串字面量"规则解码成真正的 inner JSON 文本
        // 注意：此处会把 \n 变成真实换行，这在 JSON 字符串值里是非法的，所以后面还要二次修复
        const decodedAnswerStr = JSON.parse(`"${rawAnswerStr}"`)

        // 实时解答专用：修复 inner JSON 文本中「字符串内部」的裸换行/控制字符 & 非法转义（如 \{）
        const sanitizeInnerJsonText = (text) => {
          let out = ''
          let inString = false
          let escape = false

          for (let i = 0; i < text.length; i++) {
            const ch = text[i]

            if (!inString) {
              if (ch === '"') {
                inString = true
              }
              out += ch
              continue
            }

            // inString === true
            if (escape) {
              // 仅允许 JSON 标准转义：" \ / b f n r t u
              if (ch === 'u') {
                const hex = text.slice(i + 1, i + 5)
                if (/^[0-9a-fA-F]{4}$/.test(hex)) {
                  out += '\\u' + hex
                  i += 4
                } else {
                  // 非法 unicode，保守处理：把反斜杠也转义掉
                  out += '\\\\u'
                }
              } else if ('"\\/bfnrt'.includes(ch)) {
                out += '\\' + ch
              } else {
                // 非法转义（如 \{），把反斜杠本身转义，保留原字符
                out += '\\\\' + ch
              }
              escape = false
              continue
            }

            if (ch === '\\') {
              escape = true
              continue
            }

            if (ch === '"') {
              inString = false
              out += ch
              continue
            }

            // JSON 字符串内部不能出现裸控制字符
            if (ch === '\n') {
              out += '\\n'
              continue
            }
            if (ch === '\r') {
              out += '\\r'
              continue
            }
            if (ch === '\t') {
              out += '\\t'
              continue
            }
            const code = ch.charCodeAt(0)
            if (code <= 0x1F) {
              // 其他控制字符统一剔除（也可以改成 \\u00XX）
              continue
            }

            out += ch
          }

          // 若字符串以反斜杠结尾，补上一个反斜杠避免 JSON.parse 再次报错
          if (escape) out += '\\\\'
          return out
        }

        let processedAnswerStr = sanitizeInnerJsonText(decodedAnswerStr)

        // 移除 data-bbox 属性（兼容 "..."）
        processedAnswerStr = processedAnswerStr.replace(/\s+data-bbox="[^"]*"/g, '')

        const innerData = JSON.parse(processedAnswerStr)

        // 清理 innerData 中的 MathML 内容
        if (innerData) {
          if (innerData.ocrContent) {
            innerData.ocrContent = this.cleanMathML(innerData.ocrContent)
          }

          if (innerData.testPoints) {
            innerData.testPoints = this.cleanMathML(innerData.testPoints)
          }

          if (innerData.preAnswer) {
            innerData.preAnswer = this.cleanMathML(innerData.preAnswer)
          }

          // 将外层数据添加到内层数据中
          innerData.ShootImg = outerData.ShootImg
          innerData.requestAnswerRecordId = outerData.requestAnswerRecordId
          innerData.processingTime = outerData.processingTime
        }

        return innerData
      } catch (e) {
        console.error('解析实时解答数据失败:', e.message)
        return null
      }
    },

    // 解析双层 JSON 数据（用于 kind === 3 OCR识别结果）
    parseDoubleJsonData(jsonString) {
      try {
        const outerData = JSON.parse(jsonString)
        if (outerData && outerData.answerStr) {
          // 移除 data-bbox 属性
          let cleanedAnswerStr = outerData.answerStr.replace(/\s+data-bbox="[^"]*"/g, '')

          const innerData = JSON.parse(cleanedAnswerStr)

          // 清理 innerData 中的 MathML 内容
          if (innerData) {
            if (innerData.ocrContent) {
              innerData.ocrContent = this.cleanMathML(innerData.ocrContent)
            }

            if (innerData.testPoints) {
              innerData.testPoints = this.cleanMathML(innerData.testPoints)
            }

            if (innerData.preAnswer) {
              innerData.preAnswer = this.cleanMathML(innerData.preAnswer)
            }

            // 处理 OCR 数组数据
            if (Array.isArray(innerData)) {
              innerData.forEach(item => {
                if (item.text) {
                  item.text = this.cleanMathML(item.text)
                }
              })
            }
          }

          return { outerData, innerData }
        }
        return { outerData, innerData: null }
      } catch (e) {
        console.error('解析双层 JSON 数据失败:', e)
        return { outerData: null, innerData: null }
      }
    },

    // 创建消息对象（统一消息格式）
    createMessageObject(type, content, time, extraProps = {}) {
      return {
        type,
        content,
        time,
        showTime: extraProps.showTime || false,
        avatar: type === 'sent' ? this.userAvatar : this.robotAvatar,
        ...extraProps
      }
    },

    createProcessErrorMessage(time, chatHistoryId, errorMsg, extraProps = {}) {
      return this.createMessageObject('received', errorMsg || '处理失败，请稍后重试', time, {
        id: chatHistoryId,
        isProcessError: true,
        shootImg: extraProps.shootImg || '',
        processingTime: extraProps.processingTime
      })
    },

    parseProcessErrorData(answerContent) {
      try {
        const data = typeof answerContent === 'string' ? JSON.parse(answerContent) : answerContent
        if (data && typeof data === 'object' && (data.error === true || data.success === false)) {
          return {
            errorMsg: data.errorMsg || '处理失败，请稍后重试',
            ShootImg: data.ShootImg,
            processingTime: data.processingTime
          }
        }
      } catch (e) {
        // 非 JSON 内容按普通消息处理
      }
      return null
    },

    // 解析不同类型的消息内容
    parseMessageContent(captureImgUrl,answerContent, messageType, time, chatHistoryId) {
      if (!answerContent) {
        return this.createProcessErrorMessage(time, chatHistoryId, '处理失败，请稍后重试')
      }
      console.log("captureImgUrl------------",captureImgUrl);
      const processError = this.parseProcessErrorData(answerContent)
      if (processError) {
        return this.createProcessErrorMessage(time, chatHistoryId, processError.errorMsg, {
          shootImg: processError.ShootImg || captureImgUrl,
          processingTime: processError.processingTime
        })
      }
      if (messageType === 2) {
        // 实时解答
        const answerData = this.parseRealTimeAnswerData(answerContent)
        if (answerData) {
          answerData.captureImgUrl = captureImgUrl
          return this.createMessageObject('received', answerContent, time, {
            id: chatHistoryId,
            isRealTimeAnswer: true,
            answerData: answerData
          })
        }
        return this.createProcessErrorMessage(time, chatHistoryId, '解答结果解析失败，请稍后重试')
      } else if (messageType === 3) {
        // OCR 识别结果
        const { outerData, innerData } = this.parseDoubleJsonData(answerContent)
        if (outerData) {
          // 如果 innerData 是数组且有内容，正常显示
          if (Array.isArray(innerData) && innerData.length > 0) {
            return this.createMessageObject('received', answerContent, time, {
              id: chatHistoryId,
              isOcrResult: true,
              ocrData: innerData,
              shootImg: outerData.ShootImg,
              requestErrorRecordId: outerData.requestErrorRecordId,
              processingTime: outerData.processingTime
            })
          }
          // 如果 innerData 为空或不是数组，显示为普通消息（带拍摄图片）
          return this.createMessageObject('received', '错题搜集完成', time, {
            id: chatHistoryId,
            isOcrResult: true,
            ocrData: [],
            shootImg: outerData.ShootImg,
            requestErrorRecordId: outerData.requestErrorRecordId,
            processingTime: outerData.processingTime
          })
        }
      } else if (messageType === 4) {
        // 学习计划 PDF
        return this.createMessageObject('received', answerContent, time, {
          id: chatHistoryId,
          isPdfContent: true,
          pdfUrl: answerContent
        })
      } else if (messageType === 5) {
        // 作业批改
        const homeworkData = this.parseHomeworkCorrectionData(answerContent)
        if (homeworkData) {
          return this.createMessageObject('received', homeworkData.answerStr || '作业批改完成', time, {
            id: chatHistoryId,
            isHomeworkCorrection: true,
            homeworkData,
            processingTime: homeworkData.processingTime
          })
        }
      } else if (messageType === 7) {
        // 自动出题练习卷
        const exerciseData = this.parseExercisePaperData(answerContent)
        if (exerciseData) {
          return this.createMessageObject('received', exerciseData.paperName || '练习卷已生成', time, {
            id: chatHistoryId,
            isExercisePaper: true,
            exerciseData,
            processingTime: exerciseData.processingTime
          })
        }
      }

      // 普通消息
      return this.createMessageObject('received', answerContent, time, {
        id: chatHistoryId
      })
    },

    // 解析作业批改（type=5）结果
    parseHomeworkCorrectionData(answerContent) {
      if (!answerContent) return null
      try {
        const data = typeof answerContent === 'string' ? JSON.parse(answerContent) : answerContent
        if (!data || typeof data !== 'object') {
          return {
            answerStr: typeof answerContent === 'string' ? answerContent : '作业批改完成'
          }
        }
        const answerStr = data.answerStr
        const correctionImgUrl = data.correctionImgUrl || ''
        // 新数据用 homeworkCorrectionId；历史记录兼容 requestErrorRecordId
        const homeworkCorrectionId = data.homeworkCorrectionId 
        return {
          homeworkCorrectionId,
          correctionImgUrl,
          shootImg: data.ShootImg || data.shootImg || '',
          answerStr: !answerStr || answerStr === '{}'
            ? (correctionImgUrl ? '作业批改完成' : '未识别到可批改的题目')
            : answerStr,
          processingTime: data.processingTime
        }
      } catch (e) {
        return {
          answerStr: typeof answerContent === 'string' ? answerContent : '作业批改完成'
        }
      }
    },

    // 解析自动出题（type=7）结果
    parseExercisePaperData(answerContent) {
      if (!answerContent) return null
      try {
        const data = typeof answerContent === 'string' ? JSON.parse(answerContent) : answerContent
        if (!data || typeof data !== 'object') return null
        return {
          exercisePaperId: data.exercisePaperId,
          paperName: data.paperName,
          questionCount: data.questionCount,
          pdfUrl: data.pdfUrl,
          answerPdfUrl: data.answerPdfUrl,
          tips: data.tips,
          questionList: Array.isArray(data.questionList) ? data.questionList : [],
          processingTime: data.processingTime
        }
      } catch (e) {
        console.error('解析练习卷数据失败:', e)
        return null
      }
    },

    getQuestionTypeName(questionType) {
      const map = {
        0: '综合',
        1: '填空',
        2: '选择',
        3: '判断',
        4: '计算',
        5: '连线',
        6: '应用题',
        7: '解答',
        8: '朗读背诵',
        9: '阅读理解',
        10: '写作',
        11: '翻译',
        12: '句型改写',
        13: '听写单词'
      }
      return map[questionType] || '题目'
    },

    parseOptionsContent(optionsContent) {
      if (!optionsContent) return []
      try {
        const options = typeof optionsContent === 'string' ? JSON.parse(optionsContent) : optionsContent
        return Array.isArray(options) ? options : []
      } catch (e) {
        return []
      }
    },

    formatExerciseContent(text) {
      if (!text) return ''
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>')
    },

    exerciseQuestionKey(messageId, idx) {
      return `${messageId || 'x'}-${idx}`
    },

    isExerciseQuestionExpanded(messageId, idx) {
      return !!this.expandedExerciseQuestions[this.exerciseQuestionKey(messageId, idx)]
    },

    toggleExerciseQuestion(messageId, idx) {
      const key = this.exerciseQuestionKey(messageId, idx)
      this.$set(this.expandedExerciseQuestions, key, !this.expandedExerciseQuestions[key])
      if (this.expandedExerciseQuestions[key]) {
        this.$nextTick(() => this.renderMath())
      }
    },

    isExercisePaperExpanded(messageId) {
      return !!this.expandedExercisePapers[messageId || 'x']
    },

    toggleExercisePaperList(messageId) {
      const key = messageId || 'x'
      this.$set(this.expandedExercisePapers, key, !this.expandedExercisePapers[key])
      if (this.expandedExercisePapers[key]) {
        this.$nextTick(() => this.renderMath())
      }
    },

    visibleExerciseQuestions(message) {
      const list = (message.exerciseData && message.exerciseData.questionList) || []
      if (this.isExercisePaperExpanded(message.id)) {
        return list
      }
      return list.slice(0, 1)
    },

    // 滚动到底部并渲染数学公式
    scrollAndRenderMath() {
      this.$nextTick(() => {
        this.scrollToBottom()
        this.renderMath()
      })
    },

    // 获取聊天历史记录
    async fetchChatHistory() {
      try {
        const response = await axios.get('chatHistory', {
          params: {
            pageNumber: 0,
            pageSize: 50,
            devNo: this.devNo
          }
        })

        if (response.data.code === 200 && response.data.data.list) {
          const chatList = response.data.data.list
          const messages = []

          // 先反转列表，让旧消息在前（接口返回的是降序）
          chatList.reverse().forEach((item, index) => {
            const time = item.createTime ? item.createTime.substring(11, 16) : ''

            // 添加用户问题（右边）
            if (item.chatContent) {
              messages.push(this.createMessageObject('sent', item.chatContent, time, {
                showTime: index === 0
              }))
            }

            // 添加机器人回答（左边），使用统一的消息解析方法
            if (item.answerContent) {
              const parsedMessage = this.parseMessageContent(item.captureImgUrl,item.answerContent, item.type, time, item.id)
              if (parsedMessage) {
                if (item.processingTime != null && parsedMessage.processingTime == null) {
                  parsedMessage.processingTime = item.processingTime
                }
                messages.push(parsedMessage)
              }
            }
          })

          this.messages = messages
          this.scrollAndRenderMath()
          if (this.pendingLocatePayload) {
            const pending = this.pendingLocatePayload
            this.pendingLocatePayload = null
            this.locateMessage(pending)
          }
        }
      } catch (error) {
        console.error('获取聊天历史失败:', error)
        this.$message.error('获取聊天历史失败')
      }
    },
    handleCommand(command) {
      if (command === 'search') {
        this.$router.push({ name: 'student-search' }).catch(() => {})
      } else if (command === 'reconnect') {
        console.log('[WS] 用户点击重新连接')
        this.wsReconnectAttempts = 0
        this.connectWebSocket()
      } else if (command === 'disconnect') {
        this.disconnectWebSocket()
      }
    },
    selectRecent(item) {
      if (!item) return
      this.$nextTick(() => {
        const wrappers = this.$refs.messagesContainer
          ? this.$refs.messagesContainer.querySelectorAll('.message-wrapper')
          : []
        const target = wrappers[item.messageIndex]
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    locateMessage(payload) {
      if (!payload) return
      if (!this.messages.length) {
        this.pendingLocatePayload = payload
        return
      }
      this.$nextTick(() => {
        const { chatHistoryId, content, type } = payload
        let index = -1
        if (chatHistoryId != null) {
          index = this.messages.findIndex(msg => {
            if (type === 'sent') {
              return msg.type === 'sent' && msg.content === content
            }
            return msg.id === chatHistoryId || String(msg.id) === String(chatHistoryId)
          })
        }
        if (index < 0 && content) {
          index = this.messages.findIndex(msg => (msg.content || '').includes(content.slice(0, 40)))
        }
        if (index < 0) return
        const wrappers = this.$refs.messagesContainer
          ? this.$refs.messagesContainer.querySelectorAll('.message-wrapper')
          : []
        const target = wrappers[index]
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    openSearch() {
      this.$router.push({ name: 'student-search' }).catch(() => {})
    },
    closeSearch() {
      this.searchDialogVisible = false
      this.searchKeyword = ''
    },
    clearSearch() {
      this.searchKeyword = ''
    },
    highlightKeyword(text) {
      if (!this.searchKeyword) {
        return text
      }
      const keyword = this.searchKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return text.replace(new RegExp(keyword, 'gi'), match => `<span class="highlight">${match}</span>`)
    },
    toggleChatInput() {
      this.showChatInput = !this.showChatInput
      if (this.showChatInput) {
        this.$nextTick(() => {
          if (this.$refs.chatInput) {
            this.$refs.chatInput.focus()
          }
          this.scrollToBottom()
        })
      }
    },
    sendMessage() {
      if (this.inputMessage.trim()) {
        this.messages.push(this.createMessageObject('sent', this.inputMessage, this.getCurrentTime()))
        this.inputMessage = ''
        this.scrollToBottom()
      }
    },
    getCurrentTime() {
      const now = new Date()
      return `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    showVoice() {
      this.$message.info('语音功能')
    },
    showEmoji() {
      this.$message.info('表情功能')
    },
    showMore() {
      this.$message.info('更多功能')
    },
    // WebSocket 方法
    connectWebSocket() {
      if (!this.serverUrl) {
        this.$message.error('请输入服务器地址')
        return
      }

      if (!this.devNo) {
        this.$message.error('请绑定设备，再进行连接操作。')
        return
      }

      this.wsManualClose = false
      this.clearWsReconnectTimer()

      // 已有连接且处于连接中/已连接时，先干净关闭旧连接（避免重复回调）
      if (this.websocket !== null) {
        try {
          this.websocket.onopen = null
          this.websocket.onmessage = null
          this.websocket.onerror = null
          this.websocket.onclose = null
          this.websocket.close()
        } catch (e) {
          console.warn('[WS] 关闭旧连接异常:', e)
        }
        this.websocket = null
      }

      this.stopWsHeartbeat()

      // 创建WebSocket连接，将设备号作为URL参数
      const url = `${this.serverUrl}?devNo=${this.devNo}`
      console.log('[WS] 开始连接:', url, '重连次数:', this.wsReconnectAttempts)

      try {
        this.websocket = new WebSocket(url)

        // 连接建立时触发
        this.websocket.onopen = () => {
          const isReconnect = this.wsReconnectAttempts > 0
          this.isConnected = true
          this.wsReconnectAttempts = 0
          console.log('[WS] 连接成功', isReconnect ? '(自动重连)' : '(首次/手动)')
          if (!isReconnect) {
            this.$message.success('连接成功')
          }
          this.startWsHeartbeat()
        }

        // 接收到消息时触发
        this.websocket.onmessage = (event) => {
          try {
            // 服务端心跳回包等非业务消息
            if (event.data === 'pong' || event.data === 'ping') {
              console.log('[WS] 收到心跳响应:', event.data)
              return
            }
            // 业务消息视为活跃，重置空闲屏保计时
            this.resetIdleTimer()
            const message = JSON.parse(event.data)
            this.handleWebSocketMessage(message)
          } catch (e) {
            // 非 JSON 消息不处理
            console.log('[WS] 收到非 JSON 消息:', event.data)
          }
        }

        // 连接关闭时触发
        this.websocket.onclose = (event) => {
          this.isConnected = false
          this.stopWsHeartbeat()
          console.warn(
            '[WS] 连接已断开',
            'code=', event.code,
            'reason=', event.reason || '(空)',
            'wasClean=', event.wasClean,
            'manualClose=', this.wsManualClose
          )
          if (!this.wsManualClose) {
            // 仅首次断线提示，避免重连过程中弹窗刷屏
            if (this.wsReconnectAttempts === 0) {
              this.$message.warning('连接已断开，正在尝试重连...')
            }
            this.scheduleWsReconnect()
          } else {
            this.$message.warning('连接已断开')
          }
        }

        // 发生错误时触发（随后通常会触发 onclose）
        this.websocket.onerror = (error) => {
          console.error('[WS] 连接错误:', error)
          if (this.wsReconnectAttempts === 0 && !this.isConnected) {
            this.$message.error('连接失败，请检查服务器地址和网络连接')
          }
        }
      } catch (e) {
        console.error('[WS] 创建连接异常:', e)
        this.$message.error(`连接失败: ${e.message}`)
        if (!this.wsManualClose) {
          this.scheduleWsReconnect()
        }
      }
    },
    // 心跳保活，防止长时间空闲被网关/代理断开
    startWsHeartbeat() {
      this.stopWsHeartbeat()
      this.wsHeartbeatTimer = setInterval(() => {
        if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
          try {
            this.websocket.send('ping')
            console.log('[WS] 发送心跳 ping')
          } catch (e) {
            console.warn('[WS] 发送心跳失败:', e)
          }
        } else {
          console.warn('[WS] 心跳检测：连接未就绪, readyState=', this.websocket && this.websocket.readyState)
        }
      }, this.wsHeartbeatInterval)
      console.log('[WS] 心跳已启动, 间隔=', this.wsHeartbeatInterval, 'ms')
    },
    stopWsHeartbeat() {
      if (this.wsHeartbeatTimer) {
        clearInterval(this.wsHeartbeatTimer)
        this.wsHeartbeatTimer = null
        console.log('[WS] 心跳已停止')
      }
    },
    // 断线后自动重连（指数退避，上限 30s）
    scheduleWsReconnect() {
      if (this.wsManualClose) {
        console.log('[WS] 主动断开，跳过自动重连')
        return
      }
      this.clearWsReconnectTimer()
      this.wsReconnectAttempts += 1
      const delay = Math.min(
        this.wsReconnectBaseDelay * Math.pow(2, Math.min(this.wsReconnectAttempts - 1, 4)),
        this.wsReconnectMaxDelay
      )
      console.log(`[WS] ${delay}ms 后进行第 ${this.wsReconnectAttempts} 次重连`)
      this.wsReconnectTimer = setTimeout(() => {
        this.wsReconnectTimer = null
        console.log('[WS] 执行自动重连...')
        this.connectWebSocket()
      }, delay)
    },
    clearWsReconnectTimer() {
      if (this.wsReconnectTimer) {
        clearTimeout(this.wsReconnectTimer)
        this.wsReconnectTimer = null
      }
    },
    disconnectWebSocket() {
      console.log('[WS] 主动断开连接')
      this.wsManualClose = true
      this.clearWsReconnectTimer()
      this.stopWsHeartbeat()
      if (this.websocket !== null) {
        try {
          this.websocket.onopen = null
          this.websocket.onmessage = null
          this.websocket.onerror = null
          this.websocket.onclose = null
          this.websocket.close()
        } catch (e) {
          console.warn('[WS] 主动关闭异常:', e)
        }
        this.websocket = null
        this.isConnected = false
      }
    },
    showOcrImage(imageUrl) {
      this.currentOcrImage = imageUrl
      this.ocrImageDialogVisible = true
    },
    showShootImage(url) {
      this.currentShootImage = url
      this.captureImgUrl = ''
      this.shootImageDialogVisible = true
    },
    showShootImage1(obj) {
      this.currentShootImage = obj.answerData.ShootImg
      this.captureImgUrl = obj.answerData.captureImgUrl || ''
      this.shootImageDialogVisible = true
    },
    showMoreInfo(recordId) {
      this.currentRecordId = recordId
      this.moreInfoDialogVisible = true
    },
    goToHomeworkPage() {
      this.homeworkDetailVisible = false
      if (this.$route.path !== '/student/homework-correction') {
        this.$router.push({ name: 'student-homework-correction' }).catch(() => {})
      }
    },
    goToErrorBook() {
      if (this.$route.path !== '/student/error-book') {
        this.$router.push({ name: 'student-error-book' }).catch(() => {})
      }
    },
    async openHomeworkDetail(message) {
      const homeworkData = (message && message.homeworkData) || {}
      const rawId = homeworkData.homeworkCorrectionId
      const id = rawId == null || rawId === '' ? null : Number(rawId)
      const correctionImgUrl = homeworkData.correctionImgUrl || ''
      console.log('[作业批改详情] 点击查看', {
        chatHistoryId: message && message.id,
        pageDevNo: this.devNo,
        homeworkData: JSON.parse(JSON.stringify(homeworkData)),
        rawId,
        parsedId: id,
        correctionImgUrl
      })
      if ((!id || Number.isNaN(id)) && !correctionImgUrl) {
        console.warn('[作业批改详情] 缺少批改记录 id 和批改图，跳转作业批改页')
        this.goToHomeworkPage()
        return
      }
      this.homeworkDetailVisible = true
      this.homeworkDetailLoading = true
      this.homeworkDetail = null
      this.homeworkQuestionFilter = 'all'
      const params = { devNo: this.devNo }
      if (id && !Number.isNaN(id)) {
        params.id = id
      }
      try {
        console.log('[作业批改详情] 请求参数', params)
        const response = await axios.get('homeworkCorrection/detail', { params })
        const res = response.data || {}
        console.log('[作业批改详情] 接口返回', {
          code: res.code,
          message: res.message,
          dataId: res.data && res.data.id,
          dataDevNo: res.data && res.data.devNo,
          questionCount: res.data && res.data.questionCount,
          data: res.data
        })
        if (res.code !== 200) {
          this.$message.error(res.message || '加载批改详情失败')
          this.homeworkDetailVisible = false
          return
        }
        this.homeworkDetail = res.data || null
      } catch (error) {
        console.error('[作业批改详情] 请求异常', error)
        this.$message.error('加载批改详情失败')
        this.homeworkDetailVisible = false
      } finally {
        this.homeworkDetailLoading = false
      }
    },
    // 在新窗口打开PDF
    openPdfInNewWindow(pdfUrl) {
      if (pdfUrl) {
        window.open(pdfUrl, '_blank')
      }
    },
    // 下载PDF
    downloadPdf(pdfUrl) {
      if (pdfUrl) {
        const link = document.createElement('a')
        link.href = pdfUrl
        link.download = 'study-plan.pdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    // 移除最后一个加载消息
    removeLoadingMessage() {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].isLoadingMessage) {
          this.messages.splice(i, 1)
          break
        }
      }
    },
    handleWebSocketMessage(message) {
      if (message.type === 'WAKE') {
        // 唤醒状态：显示 loading，隐藏输入框
        this.showLoading = true
      } else if (message.type === 'CLOSE') {
        // 关闭状态：隐藏 loading，显示输入框
        this.showLoading = false
      } else if (message.type === 'INFO-R') {
        // 将消息显示在右边
        this.messages.push(this.createMessageObject('sent', message.message, this.getCurrentTime()))

        // 如果 kind >= 1，在左边添加一个临时的加载消息
        if (message.kind >= 1) {
          const loadingTextMap = {
            1: '正在解答中',
            2: '实时解答',
            3: '错题搜集',
            4: '打印中',
            5: '作业批改',
            7: '正在出题'
          }
          this.messages.push(this.createMessageObject('received', '', this.getCurrentTime(), {
            isLoadingMessage: true,
            loadingText: loadingTextMap[message.kind] || '处理中'
          }))
        }

        this.scrollToBottom()
      } else if (message.type === 'INFO-L') {
        // 移除之前添加的临时加载消息
        this.removeLoadingMessage()

        try {
          // 使用统一的消息解析方法
          const parsedMessage = this.parseMessageContent(message.captureImgUrl,message.message, message.kind, this.getCurrentTime(), message.id)
          if (parsedMessage) {
            this.messages.push(parsedMessage)
            // 需要渲染数学公式的消息类型
            if (message.kind === 2 || message.kind === 3 || message.kind === 5 || message.kind === 7) {
              this.scrollAndRenderMath()
            } else {
              this.scrollToBottom()
            }
          } else {
            this.messages.push(this.createProcessErrorMessage(this.getCurrentTime(), message.id, '处理失败，请稍后重试'))
            this.scrollToBottom()
          }
        } catch (e) {
          console.error('解析解答消息失败:', e)
          this.messages.push(this.createProcessErrorMessage(this.getCurrentTime(), message.id, '处理失败，请稍后重试'))
          this.scrollToBottom()
        }
      }
    },
    // 打开拍照弹窗
    openPhotoDialog() {
      this.photoDialogVisible = true
    },
    // —— 空闲屏保 ——
    pad2(n) {
      return n < 10 ? `0${n}` : `${n}`
    },
    updateIdleClock() {
      const now = new Date()
      const weekDays = ['日', '一', '二', '三', '四', '五', '六']
      this.idleDateText = `${now.getMonth() + 1}月${now.getDate()}日 星期${weekDays[now.getDay()]}`
      this.idleTimeText = `${this.pad2(now.getHours())}:${this.pad2(now.getMinutes())}`
    },
    startIdleClock() {
      this.stopIdleClock()
      this.updateIdleClock()
      this.idleClockTimer = setInterval(() => {
        this.updateIdleClock()
      }, 1000)
    },
    stopIdleClock() {
      if (this.idleClockTimer) {
        clearInterval(this.idleClockTimer)
        this.idleClockTimer = null
      }
    },
    showIdleScreensaver() {
      this.showIdleScreen = true
      this.startIdleClock()
    },
    hideIdleScreensaver() {
      this.showIdleScreen = false
      this.stopIdleClock()
    },
    resetIdleTimer() {
      if (this.showIdleScreen) {
        this.hideIdleScreensaver()
      }
      if (this.idleTimer) {
        clearTimeout(this.idleTimer)
        this.idleTimer = null
      }
      this.idleTimer = setTimeout(() => {
        this.showIdleScreensaver()
      }, this.idleTimeoutMs)
    },
    // 未展示日期屏保时：任意触摸/点击都重置倒计时
    onUserActivity() {
      if (this.showIdleScreen) return
      this.resetIdleTimer()
    },
    clearIdleTimer() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer)
        this.idleTimer = null
      }
      this.stopIdleClock()
      this.showIdleScreen = false
    }
  },
  created() {
    this.$root.$on('chat:open-search', this.openSearch)
    this.$root.$on('chat:select-recent', this.selectRecent)
    this.$root.$on('chat:locate-message', this.locateMessage)
  },
  async mounted() {
    this.devNo = await getDevNo()
    // 获取聊天历史
    this.fetchChatHistory()
    // 设备号为空时不建立长连接
    if (this.devNo) {
      this.connectWebSocket()
    }
    // 同步最近对话给侧栏（防止 created 时序导致首屏未收到）
    this.$root.$emit('chat:recent-updated', this.recentConversations)
    // 空闲屏保：未展示日期时监听触摸/点击并启动计时
    window.addEventListener('touchstart', this.onUserActivity, { passive: true })
    window.addEventListener('click', this.onUserActivity)
    this.resetIdleTimer()
  },
  activated() {
    this.$root.$emit('chat:recent-updated', this.recentConversations)
    this.resetIdleTimer()
  },
  deactivated() {
    this.clearIdleTimer()
  },
  beforeDestroy() {
    // 组件销毁前断开 WebSocket 连接
    this.disconnectWebSocket()
    this.$root.$off('chat:open-search', this.openSearch)
    this.$root.$off('chat:select-recent', this.selectRecent)
    this.$root.$off('chat:locate-message', this.locateMessage)
    window.removeEventListener('touchstart', this.onUserActivity)
    window.removeEventListener('click', this.onUserActivity)
    this.clearIdleTimer()
  }
}
</script>

<style lang="less" scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 100%;
  background-color: #fff;
  position: relative;
}

// 空闲屏保（覆盖整个视口）
.idle-screensaver {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  cursor: pointer;
  user-select: none;
}

.idle-clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #f2efe8;
  text-shadow: 0 0 24px rgba(242, 239, 232, 0.35), 0 0 48px rgba(242, 239, 232, 0.15);
}

.idle-date {
  font-size: clamp(28px, 4.5vw, 56px);
  font-weight: 500;
  letter-spacing: 0.08em;
  line-height: 1.2;
  margin-bottom: 0.15em;
}

.idle-time {
  font-size: clamp(96px, 18vw, 220px);
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

// 顶部导航栏
.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  background-color: #fff;
  border-bottom: 1px solid #e8eaed;
  padding: 0 24px;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-title {
  font-size: 18px;
  font-weight: 500;
  color: #1f1f1f;
  letter-spacing: 0.2px;
}

.connection-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  font-size: 12px;
  color: #8e918f;
  background: #f0f4f9;
  cursor: pointer;
  user-select: none;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #8e918f;
  }

  &.connected {
    color: #137333;
    background: #e6f4ea;

    &::before {
      background: #34a853;
    }
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #dadce0;
  border-radius: 18px;
  background: #fff;
  color: #3c4043;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  i {
    font-size: 13px;
    color: #5f6368;
  }

  &:hover {
    background: #f0f4f9;
    border-color: #c4c7c5;
  }
}

.header-more-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 18px;
  background: transparent;
  color: #5f6368;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  i {
    font-size: 20px;
  }

  &:hover {
    background: #f0f4f9;
    color: #1f1f1f;
  }
}

// 消息区域
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 28px 24px 16px;
  background: #fff;

  &.has-cs-fab {
    padding-bottom: 88px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #dadce0;
    border-radius: 3px;
  }
}

.message-wrapper {
  max-width: 820px;
  margin: 0 auto 20px;
}

// 时间戳
.message-time {
  text-align: center;
  color: #8e918f;
  font-size: 12px;
  margin: 8px 0 16px;
}

// 消息项
.message-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 4px;

  &.received {
    justify-content: flex-start;

    .message-bubble {
      background-color: #f0f4f9;
      color: #1f1f1f;
    }
  }

  &.sent {
    flex-direction: row-reverse;

    .message-bubble {
      background-color: #d3e3fd;
      color: #0842a0;
    }
  }
}

// 头像
.avatar {
  width: 36px;
  height: 36px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    background: #e8eaed;
  }
}

// 消息气泡
.message-bubble {
  position: relative;
  max-width: ~"min(70%, 560px)";
  padding: 12px 16px;
  border-radius: 18px;
  word-wrap: break-word;

  .bubble-content {
    font-size: 15px;
    line-height: 1.55;
    color: inherit;
  }
}

.process-error-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background-color: #fce8e6;
  border-radius: 18px;
  padding: 16px 20px;
  min-width: 160px;
  max-width: ~"min(70%, 560px)";
  border: 1px solid #f5c6c2;

  .process-error-icon {
    color: #d93025;
    font-size: 18px;
    line-height: 1.4;
  }

  .process-error-text {
    font-size: 14px;
    color: #c5221f;
    line-height: 1.5;
  }
}

// 加载消息卡片
.loading-message-card {
  position: relative;
  background-color: #f0f4f9;
  border-radius: 18px;
  padding: 20px 24px;
  text-align: center;
  min-width: 160px;
  border: 1px solid #e8eaed;

  .loading-message-text {
    font-size: 14px;
    color: #3c4043;
    margin-bottom: 12px;
    font-weight: 500;
  }

  .loading-message-gif {
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 36px;
      height: 36px;
      object-fit: contain;
    }
  }
}

// PDF 内容卡片（学习计划等）
.pdf-content-card {
  position: relative;
  width: ~"min(85%, 560px)";
  background-color: #fff;
  border-radius: 18px;
  border: 1px solid #e8eaed;
  overflow: hidden;

  .pdf-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 18px;
    border-bottom: 1px solid #e8eaed;
    color: #d93025;
    font-size: 14px;
    font-weight: 500;
    background: #fff;

    i {
      font-size: 16px;
    }
  }

  .pdf-info {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px 18px;
    background-color: #f0f4f9;

    .pdf-icon {
      font-size: 48px;
      color: #1a73e8;
      flex-shrink: 0;
    }

    .pdf-text {
      flex: 1;

      .pdf-title {
        font-size: 16px;
        color: #1f1f1f;
        font-weight: 500;
        margin-bottom: 6px;
      }

      .pdf-desc {
        font-size: 13px;
        color: #5f6368;
      }
    }
  }

  .pdf-actions {
    display: flex;
    gap: 10px;
    padding: 14px 18px;
    border-top: 1px solid #e8eaed;
    background-color: #fff;

    .pdf-action-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 13px;
      color: #fff;
      background-color: #1a73e8;
      border-radius: 18px;
      cursor: pointer;
      text-decoration: none;
      transition: background 0.15s ease;

      i {
        font-size: 14px;
      }

      &:hover {
        background-color: #1557b0;
      }
    }
  }
}

// 实时解答卡片
.answer-card {
  position: relative;
  max-width: ~"min(92%, 920px)";
  background-color: #fff;
  border-radius: 18px;
  padding: 18px;
  border: 1px solid #e8eaed;

  .answer-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8eaed;
    color: #137333;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }

    .processing-time {
      font-size: 12px;
      color: #8e918f;
      font-weight: normal;
      margin-left: 4px;
    }

    .answer-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        font-size: 12px;
        color: #3c4043;
        background-color: #f0f4f9;
        border-radius: 14px;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;

        i {
          font-size: 13px;
        }

        &:hover {
          background-color: #e8f0fe;
          color: #1a73e8;
        }
      }
    }
  }

  .answer-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .answer-section {
    margin-bottom: 0;
    padding: 14px 16px;
    border-radius: 14px;
    background-color: #f8f9fa;

    &.question-answer-section {
      background-color: #fef7e0;
      border-left: 3px solid #f9ab00;

      &.error {
        background-color: #fce8e6;
        border-left-color: #d93025;
      }
    }

    &.analysis-section {
      background-color: #e6f4ea;
      border-left: 3px solid #34a853;

      .answer-result-inline {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 8px 10px;

        &:not(:first-child) {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #a8dab5;
        }

        .result-label {
          flex-shrink: 0;
          font-size: 12px;
          color: #137333;
          font-weight: 600;
        }

        .answer-value {
          padding: 2px 8px;
          font-size: 15px;
          color: #1f1f1f;
          font-weight: 600;
          line-height: 1.5;
          background: rgba(255, 255, 255, 0.72);
          border-radius: 6px;
        }
      }
    }

    .section-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      font-size: 14px;
      color: #1f1f1f;
      margin-bottom: 8px;
      font-weight: 600;

      .section-title-left {
        display: inline-flex;
        align-items: center;
        gap: 6px;

        i {
          font-size: 16px;
        }
      }

      .result-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px 10px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.4;

        i {
          font-size: 13px;
        }

        &.correct {
          color: #137333;
          background-color: #ceead6;
        }

        &.wrong {
          color: #c5221f;
          background-color: #f9d2d0;
        }
      }
    }

    .section-content {
      font-size: 14px;
      line-height: 1.8;
      color: #3c4043;

      /deep/ p {
        margin: 6px 0;
      }

      /deep/ ul {
        margin: 8px 0;
        padding-left: 20px;
      }

      /deep/ li {
        margin: 4px 0;
      }

      // MathJax 公式样式
      &.math-content {
        /deep/ .MathJax {
          font-size: 100% !important;
        }

        /deep/ mjx-container {
          margin: 4px 0;
          overflow-x: auto;
          overflow-y: hidden;
        }

        /deep/ mjx-container[display="true"] {
          display: block;
          margin: 10px 0;
          text-align: center;
        }
      }
    }
  }

}

// OCR 识别结果卡片
.ocr-result-card {
  position: relative;
  max-width: ~"min(85%, 640px)";
  background-color: #fff;
  border-radius: 18px;
  padding: 18px;
  border: 1px solid #e8eaed;

  .ocr-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8eaed;
    color: #1a73e8;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }

    .processing-time {
      font-size: 12px;
      color: #8e918f;
      font-weight: normal;
      margin-left: 4px;
    }

    .ocr-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        font-size: 12px;
        color: #3c4043;
        background-color: #f0f4f9;
        border-radius: 14px;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;

        i {
          font-size: 13px;
        }

        &:hover {
          background-color: #e8f0fe;
          color: #1a73e8;
        }
      }
    }
  }

  .ocr-empty {
    text-align: center;
    padding: 32px 16px;
    color: #8e918f;

    i {
      font-size: 48px;
      color: #dadce0;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }

  .ocr-items {
    .ocr-items-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: #1a73e8;
      font-weight: 600;
      margin-bottom: 12px;
      padding: 10px 14px;
      background-color: #e8f0fe;
      border-radius: 14px;

      i {
        font-size: 16px;
      }
    }

    .ocr-item {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding: 14px;
      margin-bottom: 10px;
      background-color: #f8f9fa;
      border-radius: 14px;
      border-left: 3px solid #1a73e8;

      &:last-child {
        margin-bottom: 0;
      }

      .ocr-item-number {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #1a73e8;
        color: #fff;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 600;
      }

      .ocr-text {
        flex: 1;
        font-size: 14px;
        line-height: 1.8;
        color: #3c4043;
        word-break: break-word;

        /deep/ mjx-container {
          margin: 4px 0;
          overflow-x: auto;
          overflow-y: hidden;
        }
      }

      .ocr-image-icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #1a73e8;
        border-radius: 50%;
        cursor: pointer;
        transition: background 0.15s ease;

        &:hover {
          background-color: #1557b0;
        }

        i {
          font-size: 16px;
          color: #fff;
        }
      }
    }
  }

  .ocr-footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 0 0;
    margin-top: 12px;
    border-top: 1px solid #e8eaed;

    .print-btns {
      display: inline-flex;
      align-items: center;
      gap: 10px;
    }

    .print-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #fff;
      background: #1a73e8;
      border-radius: 18px;
      cursor: pointer;
      transition: background 0.15s ease;

      i {
        font-size: 14px;
      }

      &:hover {
        background: #1557b0;
      }
    }
  }
}

// 作业批改卡片
.homework-correction-card {
  position: relative;
  max-width: ~"min(85%, 640px)";
  background-color: #fff;
  border-radius: 18px;
  padding: 18px;
  border: 1px solid #e8eaed;

  .homework-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8eaed;
    color: #0d9488;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }

    .processing-time {
      font-size: 12px;
      color: #8e918f;
      font-weight: normal;
      margin-left: 4px;
    }

    .homework-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        font-size: 12px;
        color: #3c4043;
        background-color: #f0f4f9;
        border-radius: 14px;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;

        i {
          font-size: 13px;
        }

        &:hover {
          background-color: #e6f4f1;
          color: #0d9488;
        }
      }
    }
  }

  .homework-preview {
    border-radius: 14px;
    overflow: hidden;
    background-color: #f8f9fa;
    border: 1px solid #e8eaed;

    img {
      display: block;
      width: 100%;
      max-height: 360px;
      object-fit: contain;
      background-color: #fff;
      cursor: zoom-in;
    }

    .homework-preview-caption {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 14px;
      font-size: 13px;
      color: #3c4043;
    }

    .homework-preview-hint {
      color: #8e918f;
      font-size: 12px;
    }
  }

  .homework-empty {
    text-align: center;
    padding: 32px 16px;
    color: #8e918f;

    i {
      font-size: 48px;
      color: #dadce0;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }

  .homework-footer {
    display: flex;
    justify-content: flex-end;
    padding: 12px 0 0;
    margin-top: 12px;
    border-top: 1px solid #e8eaed;

    .homework-link-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 500;
      color: #fff;
      background: #0d9488;
      border-radius: 18px;
      cursor: pointer;
      transition: background 0.15s ease;

      i {
        font-size: 14px;
      }

      &:hover {
        background: #0f766e;
      }
    }
  }
}

// 自动出题练习卷卡片
.exercise-paper-card {
  position: relative;
  max-width: ~"min(85%, 640px)";
  background-color: #fff;
  border-radius: 18px;
  padding: 18px;
  border: 1px solid #e8eaed;

  .exercise-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 14px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e8eaed;
    color: #e37400;
    font-size: 14px;
    font-weight: 500;

    i {
      font-size: 16px;
    }

    .processing-time {
      font-size: 12px;
      color: #8e918f;
      font-weight: normal;
      margin-left: 4px;
    }

    .exercise-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;

      .action-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        font-size: 12px;
        color: #3c4043;
        background-color: #f0f4f9;
        border-radius: 14px;
        cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;

        i {
          font-size: 13px;
        }

        &:hover {
          background-color: #fef7e0;
          color: #e37400;
        }
      }
    }
  }

  .exercise-summary {
    margin-bottom: 14px;
    border-radius: 14px;
    overflow: hidden;
    background-color: #fef7e0;
    border: 1px solid #f9e4a8;

    .exercise-summary-main {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 16px 18px;
    }

    .exercise-icon {
      font-size: 40px;
      color: #e37400;
      flex-shrink: 0;
    }

    .exercise-summary-text {
      flex: 1;
      min-width: 0;

      .exercise-title {
        font-size: 16px;
        color: #1f1f1f;
        font-weight: 600;
        margin-bottom: 4px;
      }

      .exercise-desc {
        font-size: 13px;
        color: #5f6368;
      }
    }

    .exercise-pdf-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 12px 18px;
      border-top: 1px solid #f9e4a8;
      background: #fff;

      .pdf-action-btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 8px 14px;
        font-size: 13px;
        color: #fff;
        background-color: #e37400;
        border-radius: 18px;
        cursor: pointer;
        transition: background 0.15s ease;

        i {
          font-size: 14px;
        }

        &:hover {
          background-color: #c26400;
        }

        &.secondary {
          color: #e37400;
          background-color: #fef7e0;

          &:hover {
            background-color: #f9e4a8;
          }
        }
      }
    }
  }

  .exercise-empty {
    text-align: center;
    padding: 28px 16px;
    color: #8e918f;

    i {
      font-size: 40px;
      color: #dadce0;
      margin-bottom: 10px;
    }

    p {
      font-size: 14px;
      margin: 0;
      color: #5f6368;
    }
  }

  .exercise-questions {
    .exercise-questions-title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: #e37400;
      font-weight: 600;
      margin-bottom: 12px;
      padding: 10px 14px;
      background-color: #fef7e0;
      border-radius: 14px;

      i {
        font-size: 16px;
      }
    }

    .exercise-question-item {
      padding: 14px;
      margin-bottom: 10px;
      background-color: #f8f9fa;
      border-radius: 14px;
      border-left: 3px solid #e37400;

      &:last-child {
        margin-bottom: 0;
      }

      &.expanded {
        background-color: #fff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
      }

      .exercise-question-head {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 8px;
        cursor: pointer;
        user-select: none;
      }

      .exercise-question-number {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #e37400;
        color: #fff;
        border-radius: 50%;
        font-size: 12px;
        font-weight: 600;
      }

      .exercise-question-meta {
        flex: 1;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        min-width: 0;

        .type-tag,
        .kp-tag,
        .diff-tag {
          display: inline-block;
          padding: 2px 8px;
          font-size: 11px;
          border-radius: 10px;
          line-height: 1.4;
        }

        .type-tag {
          color: #e37400;
          background: #fef7e0;
          font-weight: 600;
        }

        .kp-tag {
          color: #1a73e8;
          background: #e8f0fe;
          max-width: 160px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .diff-tag {
          color: #5f6368;
          background: #e8eaed;
        }
      }

      .expand-icon {
        flex-shrink: 0;
        color: #8e918f;
        font-size: 14px;
      }

      .exercise-question-content {
        font-size: 14px;
        line-height: 1.8;
        color: #3c4043;
        word-break: break-word;
        padding-left: 34px;
      }

      .exercise-options {
        padding: 8px 0 0 34px;

        .exercise-option {
          display: flex;
          gap: 6px;
          font-size: 13px;
          line-height: 1.7;
          color: #5f6368;
          margin-bottom: 2px;

          .option-label {
            flex-shrink: 0;
            font-weight: 600;
            color: #3c4043;
            min-width: 18px;
          }
        }
      }

      .exercise-question-detail {
        margin-top: 10px;
        margin-left: 34px;
        padding-top: 10px;
        border-top: 1px dashed #e8eaed;

        .detail-row {
          display: flex;
          gap: 10px;
          margin-bottom: 8px;
          font-size: 13px;
          line-height: 1.7;

          &:last-child {
            margin-bottom: 0;
          }

          .detail-label {
            flex-shrink: 0;
            padding: 0 8px;
            height: 22px;
            line-height: 22px;
            border-radius: 6px;
            font-size: 12px;
            font-weight: 600;
          }

          .detail-value {
            flex: 1;
            color: #3c4043;
            word-break: break-word;
          }

          &.answer-row .detail-label {
            color: #137333;
            background: #e6f4ea;
          }

          &.analysis-row .detail-label {
            color: #1a73e8;
            background: #e8f0fe;
          }
        }
      }
    }
  }
}

// OCR 图片预览弹窗
/deep/ .ocr-image-dialog {
  .el-dialog__body {
    padding: 10px;
    text-align: center;
  }

  .ocr-preview-image {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 4px;
  }
}

// 拍摄图片预览弹窗
/deep/ .shoot-image-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    padding: 14px 20px 10px;
    border-bottom: 1px solid #f0f0f0;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }
  }

  .el-dialog__body {
    padding: 16px 20px 20px;
  }

  .shoot-preview-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: stretch;

    &.single-image {
      grid-template-columns: 1fr;
      max-width: 640px;
      margin: 0 auto;
    }
  }

  .shoot-preview-panel {
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #f7f8fa;
    border: 1px solid #ebeef5;
    border-radius: 10px;
    overflow: hidden;
  }

  .shoot-preview-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 500;
    color: #606266;
    background: #fff;
    border-bottom: 1px solid #ebeef5;

    i {
      font-size: 14px;
      color: #409eff;
    }
  }

  .shoot-preview-frame {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    min-height: 200px;
    background: #f7f8fa;
  }

  .shoot-preview-image {
    max-width: 100%;
    max-height: 62vh;
    object-fit: contain;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
}

@media (max-width: 768px) {
  .answer-card {
    max-width: 95%;
  }

  .homework-correction-card {
    max-width: 95%;
  }

  /deep/ .shoot-image-dialog {
    .shoot-preview-grid {
      grid-template-columns: 1fr;
      gap: 12px;

      &.single-image {
        max-width: none;
      }
    }

    .shoot-preview-image {
      max-height: 45vh;
    }
  }

  /deep/ .homework-detail-dialog {
    .homework-detail-body {
      flex-direction: column;
    }

    .homework-detail-left {
      width: 100%;
    }
  }
}

// 作业批改详情弹窗
/deep/ .homework-detail-dialog {
  .homework-detail-loading,
  .homework-detail-empty {
    padding: 40px 0;
    text-align: center;
    color: #909399;
  }

  .homework-detail-body {
    display: flex;
    gap: 24px;
  }

  .homework-detail-left {
    width: 45%;
    flex-shrink: 0;
  }

  .homework-detail-image {
    width: 100%;
    border-radius: 8px;
    cursor: zoom-in;
    background: #f8f9fa;
  }

  .homework-detail-placeholder {
    height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #c0c4cc;
    background: #f2f3f5;
    border-radius: 8px;
  }

  .homework-detail-meta {
    margin-top: 12px;
    font-size: 13px;
    color: #606266;
    line-height: 1.8;
  }

  .homework-detail-right {
    flex: 1;
    min-width: 0;
  }

  .homework-question-toolbar {
    margin-bottom: 12px;
  }

  .homework-question-list {
    max-height: 460px;
    overflow-y: auto;
    padding-right: 4px;
  }

  .homework-question-item {
    border: 1px solid #ebeef5;
    border-left: 3px solid #67c23a;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 12px;

    &.wrong {
      border-left-color: #f56c6c;
      background: #fef6f6;
    }
  }

  .homework-question-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .homework-question-subject {
    font-size: 13px;
    color: #2c3e50;
    font-weight: 600;
  }

  .homework-question-point {
    font-size: 12px;
    color: #909399;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .homework-question-image {
    width: 100%;
    border-radius: 6px;
    cursor: zoom-in;
    margin-bottom: 8px;
  }

  .homework-question-text {
    font-size: 13px;
    color: #606266;
    line-height: 1.6;
    margin-bottom: 6px;
  }

  .homework-question-answer {
    font-size: 13px;
    color: #2c3e50;
    background: #f8f9fa;
    border-radius: 6px;
    padding: 8px 10px;
    line-height: 1.6;

    span {
      font-weight: 600;
    }
  }
}

// 更多信息弹窗
/deep/ .more-info-dialog {
  .more-info-content {
    padding: 10px 0;

    .info-item {
      display: flex;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #f0f0f0;

      &:last-child {
        border-bottom: none;
      }

      .info-label {
        font-size: 14px;
        color: #666;
        font-weight: 500;
        min-width: 100px;
      }

      .info-value {
        font-size: 14px;
        color: #333;
        flex: 1;
        word-break: break-all;
      }
    }
  }
}

// 底部输入栏
.chat-footer {
  padding: 8px 24px 16px;
  background: #fff;
  flex-shrink: 0;

  &.is-collapsed {
    position: absolute;
    right: 0;
    bottom: 0;
    padding: 0 20px 20px 0;
    background: transparent;
    pointer-events: none;
    z-index: 20;
  }
}

.chat-input-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  width: 100%;
}

.chat-cs-btn {
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #1a73e8 0%, #1557b0 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  pointer-events: auto;
  box-shadow: 0 4px 16px rgba(26, 115, 232, 0.38);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  i {
    font-size: 24px;
  }

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 6px 20px rgba(26, 115, 232, 0.48);
  }

  &:active {
    transform: scale(0.96);
  }

  &.active {
    background: #5f6368;
    box-shadow: 0 2px 10px rgba(95, 99, 104, 0.28);

    &:hover {
      background: #3c4043;
    }
  }
}

.chat-input {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding: 8px 10px 8px 12px;
  background-color: #fff;
  border: 1px solid #dadce0;
  border-radius: 28px;
  gap: 4px;
  box-shadow: 0 1px 6px rgba(60, 64, 67, 0.08);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #1a73e8;
    box-shadow: 0 1px 8px rgba(26, 115, 232, 0.16);
  }

  .input-icon-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #5f6368;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease;

    i {
      font-size: 20px;
    }

    &:hover {
      background: #f0f4f9;
      color: #1f1f1f;
    }
  }

  .input-box {
    flex: 1;
    min-width: 0;

    input {
      width: 100%;
      height: 40px;
      padding: 0 8px;
      border: none;
      background: transparent;
      font-size: 15px;
      color: #1f1f1f;
      outline: none;

      &::placeholder {
        color: #80868b;
      }
    }
  }

  .send-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: #1a73e8;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.15s ease, opacity 0.15s ease;

    i {
      font-size: 18px;
    }

    &:hover:not(:disabled) {
      background: #1557b0;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}

.ai-disclaimer {
  max-width: 820px;
  margin: 10px auto 0;
  color: #8e918f;
  font-size: 12px;
  line-height: 1.4;
  text-align: center;
}

// Loading 容器
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: #fff;

  .loading-gif {
    width: 40px;
    height: 40px;
    object-fit: contain;
  }
}

// 搜索弹窗样式
/deep/ .search-dialog {
  margin: 0 !important;
  height: 100vh;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    height: 100vh;
  }
}

.search-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #fff;
}

.search-header {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background-color: #fff;
  border-bottom: 1px solid #e8eaed;
  gap: 12px;

  .search-box {
    flex: 1;
    display: flex;
    align-items: center;
    background-color: #f0f4f9;
    border-radius: 24px;
    padding: 10px 16px;
    gap: 8px;

    .el-icon-search {
      font-size: 18px;
      color: #5f6368;
    }

    input {
      flex: 1;
      border: none;
      outline: none;
      font-size: 15px;
      background: transparent;
      color: #1f1f1f;

      &::placeholder {
        color: #80868b;
      }
    }

    .el-icon-circle-close {
      font-size: 18px;
      color: #5f6368;
      cursor: pointer;
    }
  }

  .cancel-btn {
    font-size: 14px;
    color: #1a73e8;
    cursor: pointer;
  }
}

.search-results {
  flex: 1;
  overflow-y: auto;
  background-color: #fff;

  .no-result {
    text-align: center;
    padding: 40px 0;
    color: #8e918f;
    font-size: 14px;
  }

  .result-item {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    border-bottom: 1px solid #e8eaed;
    cursor: pointer;

    &:hover {
      background-color: #f0f4f9;
    }

    .result-avatar {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      margin-right: 12px;
      flex-shrink: 0;
      object-fit: cover;
    }

    .result-content {
      flex: 1;
      min-width: 0;

      .result-name {
        font-size: 15px;
        color: #1f1f1f;
        margin-bottom: 4px;
      }

      .result-message {
        font-size: 13px;
        color: #8e918f;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        /deep/ .highlight {
          color: #1a73e8;
          font-weight: 500;
        }
      }
    }

    .result-date {
      font-size: 12px;
      color: #8e918f;
      flex-shrink: 0;
      margin-left: 12px;
    }
  }
}
</style>
